import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  useTheme,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  Avatar,
  ListItemAvatar,
  Tooltip,
  Stack,
  alpha,
  Card,
  CardContent,
  CardMedia,
  Button,
  Badge,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Message as MessageIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  ShoppingBag as ProductIcon,
  AccessTime as TimeIcon,
  AttachMoney as PriceIcon,
  Star as StarIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../store/authStore';
import { UserService } from '../../services/userService';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { MessageService, Conversation } from '../../services/messageService';
import { motion, AnimatePresence } from 'framer-motion';

interface ConversationsSidebarProps {
  open: boolean;
  onClose: () => void;
  productId?: string;
  productName?: string;
  sellerId?: string;
  sellerName?: string;
}

const ConversationsSidebar: React.FC<ConversationsSidebarProps> = ({
  open,
  onClose,
  productId,
  productName,
  sellerId,
  sellerName,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [userAlias, setUserAlias] = useState<string | null>(null);
  const [loadingAlias, setLoadingAlias] = useState(false);
  const [sellerAlias, setSellerAlias] = useState<string | null>(null);
  const [loadingSellerAlias, setLoadingSellerAlias] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [highlightConversationId, setHighlightConversationId] = useState<string | undefined>(undefined);
  const [newConversation, setNewConversation] = useState<Conversation | null>(null);
  const [partnerAliases, setPartnerAliases] = useState<Record<string, string>>({});
  const [conversationStatus, setConversationStatus] = useState<'new' | 'existing' | null>(null);

  // Helper to fetch seller alias
  const fetchSellerAlias = async (sellerId: string) => {
    if (!sellerId) return;
    
    try {
      setLoadingSellerAlias(true);
      
      // Try to get alias from users table
      const { data: userData, error } = await supabase
        .from('users')
        .select('alias, first_name, last_name')
        .eq('id', sellerId)
        .single();
      
      if (error) {
        console.warn('Failed to fetch seller alias:', error);
        // Fallback to SQL function if RLS blocks the query
        try {
          const { data: functionData, error: functionError } = await supabase
            .rpc('get_user_alias', { user_id: sellerId });
          
          if (!functionError && functionData) {
            setSellerAlias(functionData);
          } else {
            // Final fallback to UserService
            const alias = await UserService.getCurrentUserAlias(sellerId);
            setSellerAlias(alias);
          }
        } catch (fallbackError) {
          console.error('All fallback methods failed:', fallbackError);
          setSellerAlias(null);
        }
      } else {
        setSellerAlias(userData?.alias || null);
      }
    } catch (error) {
      console.error('Error fetching seller alias:', error);
    } finally {
      setLoadingSellerAlias(false);
    }
  };

  // Helper to get chat partner info
  const getChatPartner = (conversation: Conversation) => {
    // Use other_user field from MessageService
    if (conversation.other_user) {
      return {
        id: conversation.other_user.id,
        alias: conversation.other_user.alias,
        name: `${conversation.other_user.firstName} ${conversation.other_user.lastName}`.trim(),
        avatar: conversation.other_user.avatar,
      };
    }
    
    // Fallback: return undefined if no other_user data
    return undefined;
  };

  // Helper to get chat partner alias (with direct database fetch)
  const getChatPartnerAlias = async (conversation: Conversation) => {
    const otherUserId = conversation.user1_id === user?.id ? conversation.user2_id : conversation.user1_id;
    
    try {
      // Try to get alias from users table
      const { data: userData, error } = await supabase
        .from('users')
        .select('alias')
        .eq('id', otherUserId)
        .single();
      
      if (error) {
        console.warn('Failed to fetch partner alias:', error);
        // Fallback to SQL function
        const { data: functionData, error: functionError } = await supabase
          .rpc('get_user_alias', { user_id: otherUserId });
        
        return functionError ? null : functionData;
      } else {
        return userData?.alias || null;
      }
    } catch (error) {
      console.error('Error fetching partner alias:', error);
      return null;
    }
  };

  // Helper to fetch all partner aliases
  const fetchPartnerAliases = async (conversations: Conversation[]) => {
    if (!user?.id) return;
    
    const aliases: Record<string, string> = {};
    
    // Get all unique partner IDs
    const partnerIds = new Set<string>();
    conversations.forEach(conversation => {
      const otherUserId = conversation.user1_id === user.id ? conversation.user2_id : conversation.user1_id;
      partnerIds.add(otherUserId);
    });
    
    // Fetch aliases for all partners
    for (const partnerId of Array.from(partnerIds)) {
      try {
        const { data: userData, error } = await supabase
          .from('users')
          .select('alias')
          .eq('id', partnerId)
          .single();
        
        if (error) {
          console.warn('Failed to fetch partner alias for', partnerId, error);
          // Fallback to SQL function
          const { data: functionData, error: functionError } = await supabase
            .rpc('get_user_alias', { user_id: partnerId });
          
          aliases[partnerId] = functionError ? `User_${partnerId.substring(0, 8)}` : functionData;
        } else {
          aliases[partnerId] = userData?.alias || `User_${partnerId.substring(0, 8)}`;
        }
      } catch (error) {
        console.error('Error fetching partner alias for', partnerId, error);
        aliases[partnerId] = `User_${partnerId.substring(0, 8)}`;
      }
    }
    
    setPartnerAliases(aliases);
  };

  // Helper to format time
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 168) { // 7 days
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Create conversation for the current product
  const createConversationForProduct = async () => {
    if (!user?.id || !sellerId || !productId || !productName) return;

    try {
      setLoadingConversations(true);
      
      // First check if conversation already exists for this product
      const existingConversation = await MessageService.getConversationForProduct(sellerId, productId);
      
      if (existingConversation) {
        // Conversation already exists
        setNewConversation(existingConversation);
        setHighlightConversationId(existingConversation.id);
        setConversationStatus('existing');
        
        // Update conversations list to highlight the existing conversation
        setConversations((prev) => {
          return prev.map(conv => 
            conv.id === existingConversation.id 
              ? { ...conv, ...existingConversation } // Update with latest data
              : conv
          );
        });
      } else {
        // Create new conversation
        const conversation = await MessageService.createConversationWithProduct(
          sellerId,
          productId,
          `Inquiry about ${productName}`
        );

        setNewConversation(conversation);
        setHighlightConversationId(conversation.id);
        setConversationStatus('new');

        // Add new conversation to the top of the list
        setConversations((prev) => {
          const exists = prev.some((c) => c.id === conversation.id);
          if (exists) return prev;
          return [conversation, ...prev];
        });
      }

    } catch (error) {
      console.error('Failed to create/find conversation:', error);
    } finally {
      setLoadingConversations(false);
    }
  };

  // Fetch user alias and conversations when sidebar opens
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id || !open) return;

      try {
        setLoadingAlias(true);
        setLoadingConversations(true);
        
        // Fetch user alias
        const alias = await UserService.getCurrentUserAlias(user.id);
        setUserAlias(alias);

        // Fetch seller alias if we have sellerId
        if (sellerId) {
          await fetchSellerAlias(sellerId);
        }

        // Fetch ALL conversations where user is participant (user1_id or user2_id)
        console.log('Fetching all conversations for user:', user.id);
        const fetchedConversations = await MessageService.getConversations();
        console.log('Fetched conversations:', fetchedConversations.length);
        setConversations(fetchedConversations);

        // Fetch partner aliases for all conversations
        await fetchPartnerAliases(fetchedConversations);

        // If we have product details, create/find conversation for this specific product
        if (productId && sellerId && productName) {
          await createConversationForProduct();
        }

      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoadingAlias(false);
        setLoadingConversations(false);
      }
    };

    fetchUserData();
  }, [user?.id, open, productId, sellerId, productName]);

  // Clear conversation status after 5 seconds
  useEffect(() => {
    if (conversationStatus) {
      const timer = setTimeout(() => {
        setConversationStatus(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [conversationStatus]);

  const handleConversationClick = (conversation: Conversation) => {
    navigate(`/messages?conversation=${conversation.id}`);
    onClose();
  };

  const handleViewProduct = (conversation: Conversation) => {
    if (conversation.product?.id) {
      navigate(`/product/${conversation.product.id}`);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 450,
          maxWidth: '90vw',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          backdropFilter: 'blur(20px)',
          border: 'none',
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          p: 0,
        }}
      >
        {/* Header */}
        <Box sx={{ 
          p: 3, 
          background: 'rgba(255,255,255,0.1)', 
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255,255,255,0.2)',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MessageIcon sx={{ fontSize: 28, color: '#ffd700' }} />
              <Typography variant="h5" fontWeight="bold" sx={{ 
                color: 'white',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}>
                Conversations
              </Typography>
            </Box>
            
            <IconButton 
              onClick={onClose}
              sx={{ 
                color: 'white',
                background: 'rgba(255,255,255,0.1)',
                '&:hover': { 
                  background: 'rgba(255,255,255,0.2)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Conversation Status Label */}
        {conversationStatus && (
          <Fade in={true} timeout={800}>
            <Box sx={{ px: 3, pb: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: 2,
                borderRadius: 3,
                background: conversationStatus === 'new' 
                  ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
                  : 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)',
                border: `2px solid ${conversationStatus === 'new' ? '#4caf50' : '#2196f3'}`,
                boxShadow: `0 4px 12px ${conversationStatus === 'new' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(33, 150, 243, 0.3)'}`,
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* Animated background effect */}
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                  animation: conversationStatus === 'new' ? 'shimmer 2s infinite' : 'none',
                  '@keyframes shimmer': {
                    '0%': { left: '-100%' },
                    '100%': { left: '100%' },
                  },
                }} />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, zIndex: 1 }}>
                  {conversationStatus === 'new' ? (
                    <>
                      <Box sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: 'pulse 1.5s infinite',
                        '@keyframes pulse': {
                          '0%': { transform: 'scale(1)', opacity: 1 },
                          '50%': { transform: 'scale(1.1)', opacity: 0.7 },
                          '100%': { transform: 'scale(1)', opacity: 1 },
                        },
                      }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'white' }}>✨</Typography>
                      </Box>
                      <Typography variant="h6" sx={{ 
                        color: 'white',
                        fontWeight: 700,
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                        fontSize: '1rem',
                      }}>
                        New Conversation Created!
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Box sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'white' }}>💬</Typography>
                      </Box>
                      <Typography variant="h6" sx={{ 
                        color: 'white',
                        fontWeight: 700,
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                        fontSize: '1rem',
                      }}>
                        Existing Conversation Found!
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Fade>
        )}

        {/* User Information Display */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Box sx={{ 
            p: 2, 
            background: 'rgba(255,255,255,0.1)', 
            borderRadius: 2,
            border: '1px solid rgba(255,255,255,0.2)',
          }}>
            <Typography variant="caption" sx={{ 
              color: 'rgba(255,255,255,0.7)', 
              mb: 1, 
              display: 'block',
              fontSize: '0.7rem',
            }}>
              Your Profile
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ 
                bgcolor: '#ffd700', 
                color: '#333',
                width: 32,
                height: 32,
                fontSize: '0.8rem',
                fontWeight: 600,
              }}>
                {userAlias?.[0]?.toUpperCase() || user?.name?.[0]?.toUpperCase() || 'U'}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ 
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}>
                  {userAlias || 'No alias set'}
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.7rem',
                }}>
                  ID: {user?.id?.substring(0, 8)}...
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Conversations List */}
        <Box sx={{ flex: 1, overflow: 'hidden', px: 3, pb: 3 }}>
          <Typography variant="h6" sx={{ 
            mb: 2, 
            fontWeight: 700, 
            color: 'white',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
            px: 1,
          }}>
            All Conversations ({conversations.length})
          </Typography>
          
          {loadingConversations ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ color: '#ffd700' }} />
              <Typography variant="body2" sx={{ ml: 2, color: 'rgba(255,255,255,0.8)' }}>
                Loading conversations...
              </Typography>
            </Box>
          ) : conversations.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <ChatIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.5)', mb: 2 }} />
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                No conversations yet
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 1 }}>
                Start conversations by clicking "Make Offer" on products
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0, maxHeight: 500, overflow: 'auto' }}>
              <AnimatePresence>
                {conversations.map((conversation, index) => {
                  const chatPartner = getChatPartner(conversation);
                  const otherUserId = conversation.user1_id === user?.id ? conversation.user2_id : conversation.user1_id;
                  const partnerAlias = partnerAliases[otherUserId];
                  const partnerName = partnerAlias || chatPartner?.alias || chatPartner?.name || `User_${otherUserId.substring(0, 8)}`;
                  const partnerInitials = (partnerAlias?.[0] || chatPartner?.alias?.[0] || chatPartner?.name?.[0] || otherUserId?.[0] || 'U').toUpperCase();
                  
                  // Format last message nicely
                  let lastMessage = 'No messages yet';
                  if (conversation.last_message?.content) {
                    lastMessage = conversation.last_message.content;
                    if (lastMessage.length > 60) lastMessage = lastMessage.slice(0, 60) + '...';
                    if (lastMessage.startsWith('NEW_OFFER')) {
                      const parts = lastMessage.split('|');
                      const pricePart = parts.find(p => p.startsWith('price='));
                      const textPart = parts.find(p => p.startsWith('text='));
                      const price = pricePart ? pricePart.replace('price=', '') : undefined;
                      const note = textPart ? textPart.replace('text=', '') : undefined;
                      lastMessage = `Offer${price ? `: $${price}` : ''}${note ? ` — ${note}` : ''}`;
                    }
                  }
                  
                  const lastMessageTime = conversation.last_message?.created_at ? formatTime(conversation.last_message.created_at) : '';
                  const isLastMessageFromPartner = conversation.last_message?.sender_id !== user?.id;
                  const isHighlighted = highlightConversationId === conversation.id;

                  return (
                    <motion.div
                      key={conversation.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <ListItem disablePadding sx={{ mb: 1 }}>
                        <ListItemButton
                          onClick={() => handleConversationClick(conversation)}
                          sx={{
                            py: 2,
                            px: 2,
                            borderRadius: 2,
                            background: isHighlighted 
                              ? 'rgba(255,215,0,0.25)' 
                              : 'rgba(255,255,255,0.1)',
                            border: isHighlighted 
                              ? '3px solid rgba(255,215,0,0.8)' 
                              : '1px solid rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            '&:hover': {
                              background: isHighlighted 
                                ? 'rgba(255,215,0,0.3)' 
                                : 'rgba(255,255,255,0.2)',
                              transform: 'translateY(-2px)',
                              boxShadow: isHighlighted 
                                ? '0 6px 20px rgba(255,215,0,0.4)' 
                                : '0 4px 12px rgba(0,0,0,0.2)',
                            },
                            ...(isHighlighted && {
                              animation: 'highlightPulse 2s infinite',
                              '@keyframes highlightPulse': {
                                '0%': { 
                                  boxShadow: '0 0 0 0 rgba(255,215,0,0.7)',
                                  transform: 'scale(1)',
                                },
                                '50%': { 
                                  boxShadow: '0 0 0 8px rgba(255,215,0,0.1)',
                                  transform: 'scale(1.02)',
                                },
                                '100%': { 
                                  boxShadow: '0 0 0 0 rgba(255,215,0,0.7)',
                                  transform: 'scale(1)',
                                },
                              },
                            }),
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 50, position: 'relative' }}>
                            <Avatar 
                              src={chatPartner?.avatar} 
                              sx={{ 
                                bgcolor: '#ffd700', 
                                color: '#333',
                                width: 44, 
                                height: 44,
                                fontSize: '1rem',
                                fontWeight: 600,
                                boxShadow: '0 2px 8px rgba(255,215,0,0.3)',
                              }}
                            >
                              {partnerInitials}
                            </Avatar>
                            {isHighlighted && (
                              <Box sx={{
                                position: 'absolute',
                                top: -2,
                                right: -2,
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                background: conversationStatus === 'new' 
                                  ? 'linear-gradient(135deg, #4caf50, #66bb6a)'
                                  : 'linear-gradient(135deg, #2196f3, #42a5f5)',
                                border: '2px solid white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                animation: 'statusPulse 1.5s infinite',
                                '@keyframes statusPulse': {
                                  '0%': { transform: 'scale(1)', opacity: 1 },
                                  '50%': { transform: 'scale(1.2)', opacity: 0.8 },
                                  '100%': { transform: 'scale(1)', opacity: 1 },
                                },
                              }}>
                                <Typography sx={{ 
                                  fontSize: '0.6rem', 
                                  color: 'white',
                                  fontWeight: 'bold',
                                }}>
                                  {conversationStatus === 'new' ? '✨' : '💬'}
                                </Typography>
                              </Box>
                            )}
                          </ListItemAvatar>

                          <ListItemText
                            primary={
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                <Typography variant="subtitle1" sx={{ 
                                  fontWeight: 700, 
                                  flex: 1,
                                  color: 'white',
                                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                                }}>
                                  {partnerName}
                                </Typography>
                                {lastMessageTime && (
                                  <Typography variant="caption" sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    fontSize: '0.7rem',
                                  }}>
                                    {lastMessageTime}
                                  </Typography>
                                )}
                              </Stack>
                            }
                            secondary={
                              <Box>
                                {/* Product Info */}
                                {conversation.product && (
                                  <Box sx={{ mb: 1 }}>
                                    <Chip
                                      icon={<ProductIcon sx={{ fontSize: 12 }} />}
                                      label={conversation.product.name}
                                      size="small"
                                      sx={{ 
                                        height: 20, 
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        background: 'rgba(255,215,0,0.2)',
                                        color: '#ffd700',
                                        border: '1px solid rgba(255,215,0,0.3)',
                                        '& .MuiChip-icon': { fontSize: 12 }
                                      }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                                      <Tooltip title={`Product ID: ${conversation.product.id}`} arrow>
                                        <Typography variant="caption" sx={{ 
                                          color: 'rgba(255,255,255,0.6)', 
                                          fontSize: '0.6rem', 
                                          cursor: 'help' 
                                        }}>
                                          ID: {conversation.product.id.substring(0, 8)}...
                                        </Typography>
                                      </Tooltip>
                                      {conversation.product.price && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                          <PriceIcon sx={{ fontSize: 10, color: '#4caf50' }} />
                                          <Typography variant="caption" sx={{ 
                                            color: '#4caf50', 
                                            fontSize: '0.6rem', 
                                            fontWeight: 600 
                                          }}>
                                            ${conversation.product.price}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                )}
                                
                                {/* Subject */}
                                {conversation.subject && (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 600, 
                                      mb: 0.5,
                                      color: '#ffd700',
                                      fontSize: '0.8rem',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {conversation.subject}
                                  </Typography>
                                )}
                                
                                {/* Last Message */}
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      flex: 1,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      fontWeight: isLastMessageFromPartner ? 500 : 400,
                                      color: isLastMessageFromPartner ? 'white' : 'rgba(255,255,255,0.8)',
                                      fontSize: '0.8rem',
                                    }}
                                  >
                                    {isLastMessageFromPartner ? '' : 'You: '}{lastMessage}
                                  </Typography>
                                  {isLastMessageFromPartner && (
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: '#ffd700',
                                        flexShrink: 0,
                                        boxShadow: '0 0 4px rgba(255,215,0,0.5)',
                                      }}
                                    />
                                  )}
                                </Stack>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </List>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default ConversationsSidebar;
